/**
 * Client for GET /api/cognitory/v2/library/documents.
 */

import {
  COGNITORY_V2_LIBRARY_DOCUMENTS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export const LIBRARY_DOCUMENT_CANONICAL_STATES = [
  "processing",
  "indexed_not_serving",
  "building_library",
  "activating",
  "answerable",
  "terminal_failed",
  "deleting",
  "deleted",
] as const;

export type LibraryDocumentCanonicalState =
  (typeof LIBRARY_DOCUMENT_CANONICAL_STATES)[number];

export type GovernedLibraryDocumentSummary = {
  documentId: string;
  title: string;
  filename: string | null;
  contentType: string | null;
  documentStatus: "active" | "deactivated" | "unknown";
  currentVersionId: string | null;
  versionStatus: string | null;
  versionCreatedAt: string | null;
  governedSourceRecorded: boolean;
  /** Authoritative lifecycle projection from the backend read model. */
  canonicalState?: LibraryDocumentCanonicalState;
  artifactStatus?: string | null;
  servingFacts?: {
    runtimeEligible: boolean;
    selectionDecision: string;
    activePointerPresent: boolean | null;
    maintenanceActive: boolean | null;
    exactActiveManifestMembership: boolean | null;
  };
};

export type CognitoryLibraryClientResult =
  | {
      kind: "listed";
      requestId: string;
      documents: GovernedLibraryDocumentSummary[];
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "unable_to_load"
        | "temporary_failure";
      requestId: string | null;
    };

function isSummary(value: unknown): value is GovernedLibraryDocumentSummary {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.documentId === "string" &&
    typeof record.title === "string" &&
    typeof record.governedSourceRecorded === "boolean"
  );
}

export async function fetchGovernedLibraryDocuments(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryLibraryClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const url = new URL(`${base}${COGNITORY_V2_LIBRARY_DOCUMENTS_PATH}`);
  url.searchParams.set("cognitoryId", input.cognitoryId);

  let response: Response;
  try {
    response = await fetchImpl(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_load", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision !== "library_documents_listed") {
    return { kind: "unable_to_load", requestId };
  }

  const rawList = (body as { documents?: unknown }).documents;
  if (!Array.isArray(rawList)) {
    return { kind: "unable_to_load", requestId };
  }

  const documents: GovernedLibraryDocumentSummary[] = [];
  for (const item of rawList) {
    if (isSummary(item)) {
      documents.push(item);
    }
  }

  return {
    kind: "listed",
    requestId: requestId ?? "unknown",
    documents,
  };
}
